/* Delight Perfume — interaction layer.
   Vanilla, dependency-free. Every feature degrades to working HTML. */

(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- 1. Scroll reveal ------------------------------------------ */

  function initReveal(root = document) {
    const items = root.querySelectorAll('.dp-reveal:not([data-dp-observed])');
    if (!items.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-in'));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );

    items.forEach((el) => {
      el.setAttribute('data-dp-observed', '');
      const parent = el.parentElement;
      if (parent && parent.hasAttribute('data-dp-stagger') && !el.style.getPropertyValue('--dp-delay')) {
        const step = parseInt(parent.getAttribute('data-dp-stagger'), 10) || 70;
        const index = Array.prototype.indexOf.call(parent.children, el);
        el.style.setProperty('--dp-delay', Math.min(index, 8) * step + 'ms');
      }
      io.observe(el);
    });
  }

  /* ---------- 2. Horizontal carousels ----------------------------------- */

  function initCarousels(root = document) {
    root.querySelectorAll('[data-dp-carousel]:not([data-dp-ready])').forEach((wrap) => {
      wrap.setAttribute('data-dp-ready', '');

      const track = wrap.querySelector('[data-dp-track]');
      if (!track) return;

      // The product carousel keeps its arrows up in the section header, beside
      // the "view all" link, so fall back to the owning section when the
      // controls are not inside the carousel itself.
      const scope = wrap.closest('.dp') || wrap;
      const prev = wrap.querySelector('[data-dp-prev]') || scope.querySelector('[data-dp-prev]');
      const next = wrap.querySelector('[data-dp-next]') || scope.querySelector('[data-dp-next]');
      const controls = (prev || next) ? (prev || next).parentElement : null;

      const step = () => {
        const first = track.firstElementChild;
        if (!first) return track.clientWidth * 0.8;
        const gap = parseFloat(getComputedStyle(track).columnGap || '0') || 0;
        return first.getBoundingClientRect().width + gap;
      };

      const sync = () => {
        const max = track.scrollWidth - track.clientWidth - 2;
        const overflows = max > 2;
        const atStart = track.scrollLeft <= 2;
        const atEnd = track.scrollLeft >= max;

        if (prev) {
          prev.disabled = !overflows || atStart;
          prev.setAttribute('aria-disabled', String(prev.disabled));
        }
        if (next) {
          next.disabled = !overflows || atEnd;
          next.setAttribute('aria-disabled', String(next.disabled));
        }
        // Controls that can never do anything are noise — hide them.
        if (controls) controls.hidden = !overflows;
        wrap.toggleAttribute('data-dp-overflows', overflows);
      };

      const scrollByStep = (dir) =>
        track.scrollBy({ left: dir * step(), behavior: reduceMotion ? 'auto' : 'smooth' });

      if (prev) prev.addEventListener('click', () => scrollByStep(-1));
      if (next) next.addEventListener('click', () => scrollByStep(1));

      track.addEventListener(
        'scroll',
        () => {
          window.cancelAnimationFrame(track._dpRaf);
          track._dpRaf = window.requestAnimationFrame(sync);
        },
        { passive: true }
      );

      if ('ResizeObserver' in window) {
        new ResizeObserver(sync).observe(track);
      } else {
        window.addEventListener('resize', sync, { passive: true });
      }

      // Images settle after load and change the scrollable width.
      window.addEventListener('load', sync, { once: true });
      sync();
    });
  }

  /* ---------- 3. Tabs ---------------------------------------------------- */

  function initTabs(root = document) {
    root.querySelectorAll('[data-dp-tabs]:not([data-dp-ready])').forEach((group) => {
      group.setAttribute('data-dp-ready', '');

      const tabs = Array.from(group.querySelectorAll('[role="tab"]'));
      const panels = Array.from(group.querySelectorAll('[role="tabpanel"]'));
      if (!tabs.length) return;

      const select = (tab, focus = true) => {
        tabs.forEach((t) => {
          const on = t === tab;
          t.setAttribute('aria-selected', String(on));
          t.tabIndex = on ? 0 : -1;
        });
        panels.forEach((p) => {
          p.hidden = p.id !== tab.getAttribute('aria-controls');
        });
        if (focus) tab.focus();
        initReveal(group);
      };

      tabs.forEach((tab) => {
        tab.addEventListener('click', () => select(tab, false));
        tab.addEventListener('keydown', (e) => {
          const i = tabs.indexOf(tab);
          let target = null;
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') target = tabs[(i + 1) % tabs.length];
          if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') target = tabs[(i - 1 + tabs.length) % tabs.length];
          if (e.key === 'Home') target = tabs[0];
          if (e.key === 'End') target = tabs[tabs.length - 1];
          if (!target) return;
          e.preventDefault();
          select(target);
        });
      });
    });
  }

  /* ---------- 4. Count-up statistics ------------------------------------ */

  function initCounters(root = document) {
    const els = root.querySelectorAll('[data-dp-count]:not([data-dp-observed])');
    if (!els.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      els.forEach((el) => {
        el.textContent = el.getAttribute('data-dp-count');
      });
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          io.unobserve(el);
          const raw = el.getAttribute('data-dp-count') || '0';
          const target = parseFloat(raw.replace(',', '.')) || 0;
          const decimals = (raw.split(',')[1] || '').length;
          const start = performance.now();
          const dur = 1100;
          const tick = (now) => {
            const p = Math.min((now - start) / dur, 1);
            const eased = 1 - Math.pow(1 - p, 3);
            el.textContent = (target * eased).toFixed(decimals).replace('.', ',');
            if (p < 1) requestAnimationFrame(tick);
            else el.textContent = raw;
          };
          requestAnimationFrame(tick);
        });
      },
      { threshold: 0.5 }
    );

    els.forEach((el) => {
      el.setAttribute('data-dp-observed', '');
      io.observe(el);
    });
  }

  /* ---------- 5. Accordion ---------------------------------------------- */

  function initAccordions(root = document) {
    root.querySelectorAll('[data-dp-accordion]:not([data-dp-ready])').forEach((group) => {
      group.setAttribute('data-dp-ready', '');
      const items = Array.from(group.querySelectorAll('details'));

      items.forEach((item) => {
        const summary = item.querySelector('summary');
        if (!summary) return;
        summary.addEventListener('click', () => {
          // One open at a time keeps the list scannable.
          if (!item.open) {
            items.forEach((other) => {
              if (other !== item) other.open = false;
            });
          }
        });
      });
    });
  }

  /* ---------- Boot ------------------------------------------------------- */

  function boot(root) {
    initReveal(root);
    initCarousels(root);
    initTabs(root);
    initCounters(root);
    initAccordions(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(document), { once: true });
  } else {
    boot(document);
  }

  // Re-initialise sections re-rendered by the theme editor.
  document.addEventListener('shopify:section:load', (e) => boot(e.target));
  document.addEventListener('shopify:section:select', (e) => boot(e.target));
})();